import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Loader, LogIn, LogOut, DoorOpen } from 'lucide-react';
import { api, type ApiTicket, type CheckIn } from '../services/api';
import styles from './CheckInScreen.module.css';

interface TicketStatus {
  ticket: ApiTicket;
  status: CheckIn | { status: string };
}

export function CheckInScreen() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<TicketStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  const fetchTickets = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    setActionError(null);
    try {
      const list = await api.tickets.list(Number(eventId));
      const eligible = list.filter(t => t.status === 'valid' || t.status === 'checked_in');
      const statuses = await Promise.all(
        eligible.map(async (t) => ({
          ticket: t,
          status: await api.checkins.statusForTicket(t.id),
        }))
      );
      setTickets(statuses);
    } catch (error) {
      console.error('Failed to fetch check-in data:', error);
      setActionError('Failed to load door check-in data.');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleCheckIn = async (ticketId: number) => {
    setBusy(ticketId);
    setActionError(null);
    try {
      await api.checkins.create(ticketId);
      await fetchTickets();
    } catch (error) {
      console.error('Check-in failed:', error);
      setActionError(`Check-in failed: ${error instanceof Error ? error.message : 'unknown'}`);
    } finally {
      setBusy(null);
    }
  };

  const handleCheckOut = async (ticketId: number, checkinId: number) => {
    // Key `busy` off the TICKET id (matches `isBusy = busy === ticket.id` in the
    // render). The checkin record's own id (ci.id) is a different namespace and
    // would never equal ticket.id, silently disabling the guard below.
    setBusy(ticketId);
    setActionError(null);
    try {
      await api.checkins.checkout(checkinId);
      await fetchTickets();
    } catch (error) {
      console.error('Check-out failed:', error);
      setActionError(`Check-out failed: ${error instanceof Error ? error.message : 'unknown'}`);
    } finally {
      setBusy(null);
    }
  };

  const isCheckedIn = (s: CheckIn | { status: string }) => s.status === 'checked_in';

  const checkinCount = tickets.filter(t => isCheckedIn(t.status)).length;

  if (loading) return <div className={styles.container}><Loader size={20} className={styles.spinner} /> Loading...</div>;

  return (
    <div className={styles.container}>
      {actionError && (
        <div className={styles.errorBanner}>
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)} className={styles.errorClose}>×</button>
        </div>
      )}
      <div className={styles.headerRow}>
        <h1 className={styles.header}>Door check-in</h1>
        <Button variant="secondary" onClick={() => navigate(-1)}>← Back</Button>
      </div>

      <div className={styles.summaryGrid}>
        <Card body={`In: ${checkinCount}`} className={styles.textCenter} />
        <Card body={`Total: ${tickets.length}`} className={styles.textCenter} />
      </div>

      <div className={styles.listContainer}>
        {tickets.map(({ ticket, status }) => {
          const inDoor = isCheckedIn(status);
          const ci = status as CheckIn;
          const isBusy = busy === ticket.id;
          return (
            <Card key={ticket.id}>
              <div className={styles.ticketRow}>
                <div className={styles.ticketInfo}>
                  <span className={styles.ticketName}>{ticket.member_name || `Ticket #${ticket.id}`}</span>
                  <span className={styles.ticketStatus}>{ticket.status}</span>
                  {inDoor && ci.checked_in_at && (
                    <span className={styles.checkInTime}>
                      in {new Date(ci.checked_in_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
                <div className={styles.ticketAction}>
                  {inDoor ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={isBusy}
                      onClick={() => handleCheckOut(ticket.id, ci.id)}
                    >
                      <LogOut size={14} />
                      {isBusy ? '...' : 'Check out'}
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={isBusy}
                      onClick={() => handleCheckIn(ticket.id)}
                    >
                      <LogIn size={14} />
                      {isBusy ? '...' : 'Check in'}
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
        {tickets.length === 0 && (
          <div className={styles.emptyState}>
            <DoorOpen size={48} className={styles.emptyIcon} />
            <p>No eligible tickets for this event.</p>
          </div>
        )}
      </div>
    </div>
  );
}
